import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getInventory } from "../../services/inventoryService";
import {getProducts} from "../../services/productService";
import { AiOutlineReload, AiOutlineBarChart } from "react-icons/ai";

function InventoryOverview({ showPopup }) {
    const [inventoryStats, setInventoryStats] = useState({
        totalItems: 0,
        totalValue: 0,
        categoryCounts: {},
        recentUpdates: []
    });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchInventoryData();
    }, []);

    const fetchInventoryData = async () => {
        setIsLoading(true);
        try {
            // Fetch inventory items and products
            const inventoryResponse = await getInventory();
            const productsResponse = await getProducts();

            const inventoryItems = inventoryResponse.data || [];
            const products = Array.isArray(productsResponse) ? productsResponse :
                (productsResponse?.data || []);

            // Calculate total count
            const totalItems = inventoryItems.length;

            // Calculate total value
            let totalValue = 0;
            const categoryCounts = {};

            inventoryItems.forEach(item => {
                const product = products.find(p => p.id === item.product_id);
                if (product) {
                    // Add to total value
                    const itemValue = item.quantity * parseFloat(product.price || 0);
                    totalValue += itemValue;

                    // Add to category counts
                    const category = product.category_name || 'Uncategorized';
                    categoryCounts[category] = (categoryCounts[category] || 0) + 1;
                }
            });

            // Get 5 most recent inventory updates
            const recentUpdates = [...inventoryItems]
                .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
                .slice(0, 5);

            setInventoryStats({
                totalItems,
                totalValue,
                categoryCounts,
                recentUpdates
            });
        } catch (error) {
            console.error("Error fetching inventory data:", error);
            showPopup("Failed to load inventory overview", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return `Rs. ${parseFloat(value).toFixed(2)}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <Container>
            <Header>
                <h2>Inventory Overview</h2>
                <RefreshButton onClick={fetchInventoryData}>
                    <AiOutlineReload />
                </RefreshButton>
            </Header>

            {isLoading ? (
                <LoadingState>Loading inventory data...</LoadingState>
            ) : (
                <>
                    <OverviewStats>
                        <StatItem>
                            <StatLabel>Total Products</StatLabel>
                            <StatValue>{inventoryStats.totalItems}</StatValue>
                        </StatItem>
                        <StatItem>
                            <StatLabel>Total Value</StatLabel>
                            <StatValue>{formatCurrency(inventoryStats.totalValue)}</StatValue>
                        </StatItem>
                    </OverviewStats>

                    <SectionTitle>
                        <AiOutlineBarChart />
                        <span>Items by Category</span>
                    </SectionTitle>

                    <CategoryList>
                        {Object.keys(inventoryStats.categoryCounts).length > 0 ? (
                            Object.entries(inventoryStats.categoryCounts)
                                .sort((a, b) => b[1] - a[1])
                                .map(([category, count]) => (
                                    <CategoryItem key={category}>
                                        <CategoryName>{category}</CategoryName>
                                        <CategoryCount>{count} items</CategoryCount>
                                    </CategoryItem>
                                ))
                        ) : (
                            <EmptyState>No category data available</EmptyState>
                        )}
                    </CategoryList>

                    <SectionTitle>Recent Updates</SectionTitle>

                    <UpdatesList>
                        {inventoryStats.recentUpdates.length > 0 ? (
                            inventoryStats.recentUpdates.map((item) => (
                                <UpdateItem key={item.id}>
                                    <UpdateDetails>
                                        <UpdateName>{item.product_name}</UpdateName>
                                        <UpdateQty>Qty: {item.quantity}</UpdateQty>
                                    </UpdateDetails>
                                    <UpdateTime>{formatDate(item.updated_at)}</UpdateTime>
                                </UpdateItem>
                            ))
                        ) : (
                            <EmptyState>No recent updates</EmptyState>
                        )}
                    </UpdatesList>
                </>
            )}
        </Container>
    );
}

export default InventoryOverview;

const Container = styled.div`
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    padding: 20px;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h2 {
        font-size: 18px;
        margin: 0;
        color: #333;
    }
`;

const RefreshButton = styled.button`
    background: none;
    border: none;
    color: #777;
    cursor: pointer;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        color: #000;
    }
`;

const LoadingState = styled.div`
    text-align: center;
    padding: 30px;
    color: #777;
`;

const OverviewStats = styled.div`
    display: flex;
    gap: 15px;
    margin-bottom: 25px;
`;

const StatItem = styled.div`
    flex: 1;
    background-color: #f9f9f9;
    padding: 15px;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const StatLabel = styled.div`
    font-size: 12px;
    color: #777;
    margin-bottom: 5px;
`;

const StatValue = styled.div`
    font-size: 20px;
    font-weight: 600;
    color: #333;
`;

const SectionTitle = styled.h3`
    font-size: 14px;
    color: #555;
    margin: 20px 0 15px 0;
    display: flex;
    align-items: center;
    
    svg {
        margin-right: 6px;
    }
`;

const CategoryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;
`;

const CategoryItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background-color: #f9f9f9;
    border-radius: 4px;
`;

const CategoryName = styled.div`
    font-size: 13px;
    font-weight: 500;
`;

const CategoryCount = styled.div`
    font-size: 12px;
    color: #666;
    background-color: #eee;
    padding: 2px 8px;
    border-radius: 10px;
`;

const UpdatesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const UpdateItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border-bottom: 1px solid #f0f0f0;
    
    &:last-child {
        border-bottom: none;
    }
`;

const UpdateDetails = styled.div`
    display: flex;
    flex-direction: column;
`;

const UpdateName = styled.div`
    font-size: 13px;
    font-weight: 500;
`;

const UpdateQty = styled.div`
    font-size: 12px;
    color: #666;
`;

const UpdateTime = styled.div`
    font-size: 11px;
    color: #888;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 15px;
    color: #999;
    font-style: italic;
    font-size: 13px;
    background-color: #f9f9f9;
    border-radius: 4px;
`;